import { arrayCreateSuite, styleEm } from "@tevari/helpers";
import { useTranslation } from "next-i18next";
import { FunctionComponent, memo, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import styles from "./SubcriteriumModal.module.scss";

import SymbolIcon from "@components/generic/symbolIcon";
import BrandSources from "@components/referential/brandSources";
import { addPercentageToText, eSymbol } from "@lib/helper/symbolUtils";
import { IBrandSource } from "@lib/store/models/brandsModels";
import { ISubcriteriumNode } from "@lib/store/models/nodesModels";
import { useReferential } from "context/referentialContext";

export interface ISubcriteriumModalProps {
  criteriumName: string;
  criteriumId: string;
  symbol?: eSymbol;
  percentage?: number;
  brandSources: IBrandSource[];
}

const SubcriteriumModal: FunctionComponent<ISubcriteriumModalProps> = ({
  criteriumName,
  criteriumId,
  symbol,
  percentage,
  brandSources,
}) => {
  const { t } = useTranslation("brand");
  const [isLoading, setIsLoading] = useState(false);
  const [subcriteriums, setSubcriteriums] = useState<ISubcriteriumNode[]>([]);

  const { getSubcriteriums } = useReferential();

  useEffect(() => {
    setIsLoading(true);
    getSubcriteriums(criteriumId)
      .then(setSubcriteriums)
      .finally(() => setIsLoading(false));
  }, [getSubcriteriums, criteriumId]);

  const displaySubtitle: boolean = subcriteriums.length > 1;

  const _renderSpecificText = (specificText: string, percentage?: number, symbol?: eSymbol): JSX.Element => {
    const text = addPercentageToText(t("element.unknownPercentage"), specificText, percentage, symbol);
    return <p className={styles["subcriterium-modal__subcriterium-text"]}>{text}</p>;
  };

  const _renderGenericDescription = (genericDescription: string): JSX.Element => {
    return <p className={styles["subcriterium-modal__subcriterium-text"]}>{genericDescription}</p>;
  };

  const _renderSubCriterium = (subcriterium: ISubcriteriumNode): JSX.Element => {
    const { specificText, genericDescription, percentage, symbol } = subcriterium;
    return (
      <div key={subcriterium.id} className={styles["subcriterium-modal__subcriterium"]}>
        {!!displaySubtitle && <h4 className={styles["subcriterium-modal__subcriterium-title"]}>{subcriterium.name}</h4>}
        {_renderSpecificText(specificText, percentage, symbol)}
        {_renderGenericDescription(genericDescription)}
      </div>
    );
  };

  const _renderSkeleton = () => (
    <>
      {arrayCreateSuite(2).map((index) => (
        <div key={index} className={styles["subcriterium-modal__skeleton"]}>
          <Skeleton width="100%" height={styleEm(16)} count={5} />
        </div>
      ))}
    </>
  );

  return (
    <div className={styles["subcriterium-modal"]}>
      <div className={styles["subcriterium-modal__header"]}>
        <h3 className={styles["subcriterium-modal__header__text"]}>{criteriumName}</h3>
        <div className={styles["subcriterium-modal__header__icon"]}>
          {symbol && <SymbolIcon symbol={symbol} percentage={percentage} />}
        </div>
      </div>
      {isLoading ? (
        _renderSkeleton()
      ) : (
        <>
          {subcriteriums.map((subcriterium) => _renderSubCriterium(subcriterium))}
          <BrandSources sources={brandSources} />
        </>
      )}
    </div>
  );
};

export default memo(SubcriteriumModal);
